import { useEffect, useState } from 'react';
import { formatDate, formatEuro } from '@brimo/shared';
import { Banner, Button, Checkbox, Dialog, Select, TextField, Textarea } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { createInvestmentCalculation, useContactDeals, useUnitCalculations } from '../api';
import type { Calculation, CalculatorContext, InvestmentInputValues } from '../types';
import { pct, sgn, type CalculatorView } from './model';
import styles from './calculator.module.css';

export interface SaveDialogProps {
  open: boolean;
  /** `pdf` = „An Mandanten senden" (header menu „PDF") and ResultsTab's „Als PDF für den Mandanten": the PDF is sent in any case. */
  mode: 'save' | 'pdf';
  context: CalculatorContext;
  values: InvestmentInputValues;
  view: CalculatorView;
  onClose: () => void;
  onSaved: (calculation: Calculation) => void;
}

/** The prefilled „Bezeichnung" of a new version; „PDF speichern" saves under the same label. */
export function defaultLabel(context: CalculatorContext, values: InvestmentInputValues): string {
  const variant = values.sellerPaysAcquisitionCosts
    ? 'Verkäufer zahlt Kaufnebenkosten'
    : values.financingSharePct !== null
      ? `${pct(values.financingSharePct, 0)} finanziert`
      : values.equityCents <= 0
        ? '110 % finanziert'
        : `Eigenkapital ${formatEuro(values.equityCents)}`;
  return `Berechnung ${context.unit.number} · Variante ${variant}`;
}

/** IR-03 „Im Deal speichern": deal, label, note, the saved figures and the PDF checkbox → `POST /calculations`. */
export function SaveDialog({ open, mode, context, values, view, onClose, onSaved }: SaveDialogProps) {
  const { unit, contact } = context;
  const deals = useContactDeals(open && contact ? contact.id : null);
  const calculations = useUnitCalculations(unit.id, open);
  const [dealId, setDealId] = useState<string>(context.deal?.id ?? '');
  const [label, setLabel] = useState('');
  const [note, setNote] = useState('');
  const [sendPdf, setSendPdf] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (open) {
      setDealId(context.deal?.id ?? '');
      setLabel(defaultLabel(context, values));
      setNote('');
      setSendPdf(true);
      setError(null);
      setFields({});
    }
    // The defaults are taken once per opening; the form keeps the user's edits afterwards.
  }, [open]);

  const dealOptions = (deals.data ?? []).map((deal) => ({
    value: deal.id,
    label: `${deal.title} · ${deal.meta}`,
  }));
  if (context.deal && !dealOptions.some((option) => option.value === context.deal?.id)) {
    dealOptions.unshift({
      value: context.deal.id,
      label: `${contact?.name ?? ''} · ${unit.number} · ${context.deal.stageName}`,
    });
  }
  const previous =
    calculations.data?.items.find((item) =>
      dealId ? item.dealId === dealId : item.contactId === contact?.id,
    ) ?? null;
  const nextVersion = previous ? previous.version + 1 : 1;
  const versionHint = previous
    ? `Version ${nextVersion} · ersetzt die Berechnung vom ${formatDate(previous.createdAt)}`
    : 'Version 1 · erste Berechnung für diesen Mandanten';
  const mustSend = mode === 'pdf';

  async function submit() {
    if (!contact) {
      setError('Bitte zuerst einen Mandanten wählen.');
      return;
    }
    setPending(true);
    setError(null);
    setFields({});
    try {
      const calculation = await createInvestmentCalculation({
        contactId: contact.id,
        unitId: unit.id,
        ...(dealId ? { dealId } : {}),
        label: label.trim() || null,
        note: note.trim() || null,
        inputs: values,
        sendPdf: mustSend || sendPdf,
      });
      onSaved(calculation);
    } catch (err) {
      if (err instanceof ApiError && Object.keys(err.fields).length > 0) setFields(err.fields);
      else setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => (pending ? undefined : onClose())}
      eyebrow={`Berechnung · ${unit.number} · ${unit.propertyName}`}
      title={mustSend ? 'An Mandanten senden' : 'Im Deal speichern'}
      width={640}
      footerNote={versionHint}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button loading={pending} onClick={() => void submit()}>
            {mustSend ? 'Speichern und senden' : 'Speichern'}
          </Button>
        </>
      }
    >
      <div className={styles.dialogForm}>
        <div className={styles.dialogText}>
          Die Berechnung wird als Dokument im Deal abgelegt und in der Zeitleiste vermerkt. Späteres
          Bearbeiten legt eine neue Version an.
        </div>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        {!contact ? (
          <Banner tone="warning">
            Wählen Sie unter „Angaben“ einen Mandanten, damit die Berechnung gespeichert werden kann.
          </Banner>
        ) : null}
        <Select
          label="Deal"
          value={dealId}
          placeholder={dealOptions.length === 0 ? 'Ohne Deal · beim Kontakt ablegen' : 'Ohne Deal'}
          options={dealOptions}
          error={fields['dealId']}
          onChange={(event) => setDealId(event.target.value)}
        />
        <TextField
          label="Bezeichnung"
          value={label}
          error={fields['label']}
          onChange={(event) => setLabel(event.target.value)}
        />
        <Textarea
          label="Notiz für die Zeitleiste"
          rows={2}
          value={note}
          placeholder={`Eigenkapital ${formatEuro(view.result.equityCents)}, Zins ${pct(values.interestPct)}, Verkauf in Jahr ${values.saleYear}.`}
          onChange={(event) => setNote(event.target.value)}
        />
        <div className={styles.metricsBox}>
          <div className={styles.miniHead}>Kennzahlen, die mitgespeichert werden</div>
          <div className={styles.metricsGrid}>
            <div className={styles.metricRow}>
              <span className={styles.metricLabel}>Kaufpreis</span>
              <span className={styles.metricValue}>{formatEuro(values.purchasePriceCents)}</span>
            </div>
            <div className={styles.metricRow}>
              <span className={styles.metricLabel}>Eigenkapital</span>
              <span className={styles.metricValue}>{formatEuro(view.result.equityCents)}</span>
            </div>
            <div className={styles.metricRow}>
              <span className={styles.metricLabel}>Cashflow nach Steuer</span>
              <span className={styles.metricValue}>{sgn(view.monthly.cashflowAfterTaxCents)} € mtl.</span>
            </div>
            <div className={styles.metricRow}>
              <span className={styles.metricLabel}>Zins, Tilgung</span>
              <span className={styles.metricValue}>
                {pct(values.interestPct)} · {pct(values.repaymentPct)}
              </span>
            </div>
          </div>
        </div>
        <Checkbox
          label={`PDF zusätzlich an ${contact?.name ?? 'den Mandanten'} senden`}
          checked={mustSend || sendPdf}
          disabled={mustSend}
          onChange={(event) => setSendPdf(event.target.checked)}
        />
      </div>
    </Dialog>
  );
}
